import {ControlButton, TscButtonType, TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import Constants from '../../constants/Constants';
import cssConstants from '../../constants/cssConstants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore} from '../../context/mediaProvider';
import {useTscTheme} from '../../context/themeProvider';
import Analytics, {IUaEventOptions, IUAEventParams} from '../../service/analytics';
import SnagitLogoSvg from '../../static/svg/SnagitLogo.svg';
import PlusGlyph from '../../static/svg/PlusGlyph.svg';
import CamtasiaScreencastLogo from '../../static/svg/CamtasiaScreencastLogo.svg';
import ScreencastMarketingLogo from '../../static/svg/ScreencastMarketingLogo.svg';
import SnagitScreencastLogo from '../../static/svg/SnagitScreencastLogo.svg';
import CamtasiaLogoSvg from '../../static/svg/CamtasiaLogo.svg';
import {setupCSSHeightObserver} from '../../util/CSSHeightObserver';
import {showWatchPageSnagitMessage, showWatchPageCaptureMessage, showWatchPageCamtasiaMessage, showWatchPageWebUploadMessage, showWatchPageCamtasiaWebMessage} from '../../util/MarketingHelper';

interface IBannerContent {
   titleText: string;
   bodyText: string;
   CTAButtonText: string;
   CTALink: string;
}

const MarketingPane = styled.div<{theme: TscThemeNames; camtasiaBanner: boolean}>`
   box-shadow: ${props => props.camtasiaBanner ? cssConstants.marketingPaneBoxShadowCamtasia : (props.theme === TscThemeName.dusk ? cssConstants.marketingPaneBoxShadowDusk : cssConstants.marketingPaneBoxShadowDawn)};
   font-family: sans-serif;
   display: flex;
   background: ${props => props.theme === TscThemeName.dusk ? (props.camtasiaBanner ? cssConstants.marketingPaneBgCamtasiaDusk : cssConstants.marketingPaneBgDusk) : (props.camtasiaBanner ? cssConstants.marketingPaneBgCamtasiaDawn : cssConstants.marketingPaneBgDawn)};
   background-size: cover;
   background-origin: border-box;
   border-radius: 200px;
   border: ${props => props.theme === TscThemeName.dusk ? (props.camtasiaBanner ? cssConstants.marketingPaneBorderCamtasiaDusk : cssConstants.marketingPaneBorderDusk) : (props.camtasiaBanner ? cssConstants.marketingPaneBorderCamtasiaDawn : cssConstants.marketingPaneBorderDawn)};
   -webkit-box-align: center;
   align-items: center;
   align-content: flex-end;
   height: 1.875rem;
   padding: 0.5rem 0.5rem 0.5rem 1rem;
   margin: 0.8125rem 0.5rem .75rem .5rem;
   color: ${props => props.theme === TscThemeName.dusk || props.camtasiaBanner ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   width: fit-content;

   @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
      min-width: 32rem;
   }

   @media screen and (min-width: ${cssConstants.marketingPaneBreakpoint}px) {
      min-width: 44rem;
      margin-top: 0;
   }

`;

const MarketingContainer = styled.span`
   display: flex;
   -webkit-box-align: center;
   align-items: center;

   .fullsizeProductLogo {
      display: none;
      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
         display: flex;
      }
   }

   .smallProductLogo {
      display: flex;
      margin-right: .5rem;
      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
         display: none;
      }
   }
`;

const TextContainer = styled.span`
   display: flex;
   flex-direction: column;
`;

const ButtonContainer = styled.div<{camtasiaBanner: boolean}>`
   display: flex;
   flex: 0 0 auto;
   align-items: center;
   margin-left: auto;
   -webkit-box-align: center;

   .bannerCtaButton {
      border-radius: 27px;
      background-color: ${props => props.camtasiaBanner ? TscStyles.color.brand.camtasia : ''};
      color: ${props => props.camtasiaBanner ? TscStyles.color.text.dark : ''};
      justify-content: center;
      letter-spacing: 0;
      margin: 0 0 0 1rem;
      padding: 0 1rem;

      @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
         display: flex;
      }
   }
`;

const IconSvg = styled.span`
   display:flex;

   svg {
      height: 32px;
      width: 32px;
   }
`;

const TitleText = styled.span`
   font-weight: 600;
   font-size: .875rem;
   font-family: Figtree, sans-serif;
   line-height: 1rem;
   width: auto;

   @media screen and (min-width: ${cssConstants.marketingPaneBreakpoint}px) {
      margin-right: .5rem;
   }

   @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
      font-size: 1rem;
   }
`;

const DetailText = styled.span<{captureBanner: boolean}>`
   display: none;
   font-family: Figtree, sans-serif;
   margin-top: .25rem;
   font-size: .75rem;
   line-height: .75rem;
   font-weight: 500;

   @media screen and (min-width: ${cssConstants.marketingPaneShowDetailTextBreakpoint}px) {
      display: ${props => props.captureBanner ? 'none' : 'flex'};
   }

   @media screen and (min-width: ${cssConstants.marketingPaneBreakpoint}px) {
      display: flex;
   }
`;

const Logo = styled.span`
   display: flex;
   flex-direction: row;
   margin-right: 0.5rem;
`;

const PlusSignSvg = styled.span`
   display: flex;
   flex-direction: column;
   justify-content: center;
   margin: 0 0.25rem;
   color: red;
`;

const StyledPlusGlyph = styled(PlusGlyph)<{theme: TscThemeNames}>`
   fill: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dawn.lightMedium : TscStyles.color.ui.dusk.medium};
`;

const getMarketingLogo = (theme: TscThemeNames): React.JSX.Element => {
   let plusSignTheme: TscThemeNames;
   let ProductSvgLogo: React.JSX.Element;
   let SmallProductSvgLogo: React.JSX.Element;
   if (showWatchPageSnagitMessage() || showWatchPageWebUploadMessage() || showWatchPageCaptureMessage()) {
      plusSignTheme = theme;
      ProductSvgLogo = <SnagitLogoSvg />;
      SmallProductSvgLogo = <SnagitScreencastLogo />;
   } else if (showWatchPageCamtasiaMessage() || showWatchPageCamtasiaWebMessage()) {
      plusSignTheme = TscThemeName.dusk;
      ProductSvgLogo = <CamtasiaLogoSvg />;
      SmallProductSvgLogo = <CamtasiaScreencastLogo />;
   } else {
      plusSignTheme = theme;
      ProductSvgLogo = <SnagitLogoSvg />;
      SmallProductSvgLogo = <SnagitScreencastLogo />;
   }

   return (
      <>
         <Logo className="fullsizeProductLogo">
            <IconSvg>
               {ProductSvgLogo}
            </IconSvg>
            <PlusSignSvg>
               <StyledPlusGlyph theme={plusSignTheme} />
            </PlusSignSvg>
            <IconSvg>
               <ScreencastMarketingLogo />
            </IconSvg>
         </Logo>
         <Logo className="smallProductLogo">
            <IconSvg>
               {SmallProductSvgLogo}
            </IconSvg>
         </Logo>
      </>
   );
};

export const MarketingPaneBase: React.FC<IStateMappedProps> = ({Hash, IsVideo}) => {
   const marketingPaneRef = React.createRef<HTMLDivElement>();

   const {t} = useTranslation();
   const theme = useTscTheme();
   const [bannerVariantIndex, setBannerVariantIndex] = useState<number>(0);
   const isSnagitBanner = showWatchPageSnagitMessage();
   const isCaptureBanner = showWatchPageCaptureMessage();
   const isCamtasiaBanner = showWatchPageCamtasiaMessage();
   const isCamtasiaWebBanner = showWatchPageCamtasiaWebMessage();
   const isWebUploadBanner = showWatchPageWebUploadMessage();
   const [bannerContent, setBannerContent] = useState<IBannerContent>(null);
   const variantLetters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'];

   const getBannerContentVariants = (): IBannerContent[] => {
      const bannerContentVariants: IBannerContent[] = [];
      if (isSnagitBanner) {
         bannerContentVariants.push({
            titleText: t('marketing.snagit.titleText').replace('{{mediaType}}', IsVideo ? t('marketing.mediaType.video') : t('marketing.mediaType.image')),
            bodyText: null,
            CTAButtonText: t('marketing.snagit.CTAButtonText'),
            CTALink: Constants.navigation.banners.workPlaceComms
         });
         bannerContentVariants.push({
            titleText: t('marketing.snagit.titleText').replace('{{mediaType}}', IsVideo ? t('marketing.mediaType.video') : t('marketing.mediaType.image')),
            bodyText: null,
            CTAButtonText: t('marketing.snagit.CTAButtonText'),
            CTALink: IsVideo ? Constants.navigation.banners.screenRecorder : Constants.navigation.banners.screenCapture
         });
      } else if (isWebUploadBanner) {
         bannerContentVariants.push({
            titleText: t('marketing.webupload.titleText').replace('{{mediaType}}', IsVideo ? t('marketing.mediaType.video') : t('marketing.mediaType.image')),
            bodyText: null,
            CTAButtonText: t('marketing.webupload.CTAButtonText'),
            CTALink: IsVideo ? Constants.navigation.banners.screenRecorder : Constants.navigation.banners.screenCapture
         });
         bannerContentVariants.push({
            titleText: t('marketing.webupload.titleText').replace('{{mediaType}}', IsVideo ? t('marketing.mediaType.video') : t('marketing.mediaType.image')),
            bodyText: null,
            CTAButtonText: t('marketing.webupload.CTAButtonText'),
            CTALink: Constants.navigation.banners.workPlaceComms
         });
         if (IsVideo) {
            bannerContentVariants.push({
               titleText: t('marketing.webupload.titleText').replace('{{mediaType}}', t('marketing.mediaType.video')),
               bodyText: null,
               CTAButtonText: t('marketing.webupload.CTAButtonText'),
               CTALink: Constants.navigation.banners.screenCapture
            });
         }
      } else if (isCaptureBanner) {
         bannerContentVariants.push({
            titleText: t('marketing.capture.titleText').replace('{{mediaType}}', IsVideo ? t('marketing.mediaType.video') : t('marketing.mediaType.image')),
            bodyText: null,
            CTAButtonText: t('marketing.capture.CTAButtonText'),
            CTALink: Constants.navigation.banners.workPlaceComms
         });
         bannerContentVariants.push({
            titleText: t('marketing.capture.titleText').replace('{{mediaType}}', IsVideo ? t('marketing.mediaType.video') : t('marketing.mediaType.image')),
            bodyText: null,
            CTAButtonText: t('marketing.capture.CTAButtonText'),
            CTALink: IsVideo ? Constants.navigation.banners.screenRecorder : Constants.navigation.banners.screenCapture
         });
      } else if (isCamtasiaBanner) {
         bannerContentVariants.push({
            titleText: t('marketing.camtasia.titleText'),
            bodyText: null,
            CTAButtonText: t('marketing.camtasia.CTAButtonText'),
            CTALink: Constants.navigation.banners.camtasiaProduct
         });
         bannerContentVariants.push({
            titleText: t('marketing.camtasia.titleText'),
            bodyText: null,
            CTAButtonText: t('marketing.camtasia.CTAButtonText'),
            CTALink: Constants.navigation.banners.camtasiaRev
         });
         bannerContentVariants.push({
            titleText: t('marketing.camtasia.titleText'),
            bodyText: null,
            CTAButtonText: t('marketing.camtasia.CTAButtonText'),
            CTALink: Constants.navigation.banners.camtasiaBestVideoEditing
         });
      } else if (isCamtasiaWebBanner) {
         bannerContentVariants.push({
            titleText: t('marketing.camtasiaweb.titleText'),
            bodyText: null,
            CTAButtonText: t('marketing.camtasiaweb.CTAButtonText'),
            CTALink: Constants.navigation.banners.camtasiaWeb
         });
         bannerContentVariants.push({
            titleText: t('marketing.camtasiaweb2.titleText'),
            bodyText: null,
            CTAButtonText: t('marketing.camtasiaweb2.CTAButtonText'),
            CTALink: Constants.navigation.banners.camtasiaWeb
         });
      }
      return bannerContentVariants;
   };

   useEffect(() => {
      const bannerContentVariants = getBannerContentVariants();
      const variantIndex = Math.floor(Math.random() * bannerContentVariants.length);
      setBannerContent(bannerContentVariants[variantIndex]);
      setBannerVariantIndex(variantIndex);
      recordGABannerDisplayedEvent(variantIndex);
   }, []);

   const recordGABannerButtonClickedEvent = () => {
      const eventName = isSnagitBanner ? Constants.analytics.SnagitBannerButtonClicked : isCamtasiaBanner ? Constants.analytics.CamtasiaBannerButtonClicked : isCamtasiaWebBanner ? Constants.analytics.CamtasiaWebBannerButtonClicked : isCaptureBanner ? Constants.analytics.CaptureBannerButtonClicked : isWebUploadBanner ? Constants.analytics.WebUploadBannerButtonClicked : null;
      /* eslint-disable camelcase */
      const ga4Event: IUaEventOptions = {
         eventName: eventName,
         eventParams: {
            banner_variant: `${bannerVariantIndex + 1}`,
            media_hash: Hash
         } as IUAEventParams
      };
      /* eslint-enable */

      Analytics.gaEvent(ga4Event);
   };

   const recordGABannerDisplayedEvent = (bannerTypeNum: number) => {
      const eventName = isSnagitBanner ? Constants.analytics.SnagitBannerDisplayed : isCamtasiaBanner ? Constants.analytics.CamtasiaBannerDisplayed : isCamtasiaWebBanner ? Constants.analytics.CamtasiaWebBannerDisplayed : isCaptureBanner ? Constants.analytics.CaptureBannerDisplayed : isWebUploadBanner ? Constants.analytics.WebUploadBannerDisplayed : null;
      /* eslint-disable camelcase */
      const ga4Event: IUaEventOptions = {
         eventName: eventName,
         eventParams: {
            banner_variant: `${bannerTypeNum + 1}`,
            media_hash: Hash
         } as IUAEventParams
      };
      /* eslint-enable */

      Analytics.gaEvent(ga4Event);
   };

   const addUtmToLink = (link: string): string => {
      const variantLetter: string = bannerVariantIndex < variantLetters.length ? variantLetters[bannerVariantIndex] : variantLetters[0];
      const productId: string = isCamtasiaBanner ? Constants.navigation.banners.utmProductIdentifier.camtasia : isCamtasiaWebBanner ? Constants.navigation.banners.utmProductIdentifier.camtasiaweb : isSnagitBanner ? Constants.navigation.banners.utmProductIdentifier.snagit : isCaptureBanner ? Constants.navigation.banners.utmProductIdentifier.capture : Constants.navigation.banners.utmProductIdentifier.web;
      const mediaType: string = IsVideo ? 'video' : 'image';
      const utm = `?utm_source=product&utm_medium=screencastmod&utm_campaign=screencastbanners&utm_content=${productId}-${mediaType}&utm_term=variant-${variantLetter}`;
      return `${link}${utm}`;
   };

   const navigateToMarketingPage = (ctaLink: string) => {
      recordGABannerButtonClickedEvent();
      window.open(ctaLink, '_blank');
   };

   setupCSSHeightObserver(marketingPaneRef, cssConstants.variables.marketingBannerHeight);

   return (
      <MarketingPane
         theme={theme}
         ref={marketingPaneRef}
         camtasiaBanner={isCamtasiaBanner || isCamtasiaWebBanner}
         data-test-id="marketing-pane"
      >
         <MarketingContainer>
            {getMarketingLogo(theme)}
            <TextContainer>
               {bannerContent?.titleText && (
                  <TitleText>
                     <span>
                        {bannerContent.titleText}
                     </span>
                  </TitleText>
               )
               }
               {bannerContent?.bodyText &&
                  (
                     <DetailText captureBanner={isCaptureBanner}>
                        <span>
                           {bannerContent.bodyText}
                        </span>
                     </DetailText>
                  )
               }
            </TextContainer>
         </MarketingContainer>
         {bannerContent?.CTAButtonText && (
            <ButtonContainer camtasiaBanner={isCamtasiaBanner || isCamtasiaWebBanner}>
               <ControlButton
                  className="bannerCtaButton"
                  buttonType={TscButtonType.primary}
                  testId="marketing-call-to-action"
                  label={bannerContent.CTAButtonText}
                  onClick={() => navigateToMarketingPage(addUtmToLink(bannerContent.CTALink))}
               />
            </ButtonContainer>
         )}
      </MarketingPane>
   );
};

export interface IStateMappedProps {
   Hash: string;
   IsVideo: boolean;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {Hash, IsVideo}: Partial<IStateMappedProps>
): IStateMappedProps => ({Hash, IsVideo});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(MarketingPaneBase);
